import React, { useState, ReactNode } from 'react';
import { TextInput, View, ViewStyle, TextStyle, TouchableOpacity, Platform } from 'react-native';
import { Text } from './Text';
import colors from '@theme/colors';

export interface InputProps {
  // Core props
  value?: string;
  onChangeText?: (text: string) => void;
  placeholder?: string;
  placeholderTextColor?: string;
  editable?: boolean;
  isDisabled?: boolean;
  testID?: string;

  // Styling props
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number | string;
  borderRadius?: number | string;
  height?: number | string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'unstyled' | 'outline' | 'filled';

  // Text styling
  color?: string;
  fontSize?: number;
  fontWeight?: string | number;
  fontFamily?: string;

  // Layout props
  margin?: number | string;
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  marginX?: number | string;
  marginY?: number | string;
  padding?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
  paddingX?: number | string;
  paddingY?: number | string;

  // Focus/blur handling
  onFocus?: () => void;
  onBlur?: () => void;
  _focus?: { borderColor?: string; backgroundColor?: string };

  // Elements
  InputLeftElement?: ReactNode;
  InputRightElement?: ReactNode;

  // Web-specific props
  _web?: any;

  // All other TextInput props
  [key: string]: any;
}

const Input: React.FC<InputProps> = ({
  value,
  onChangeText,
  placeholder,
  placeholderTextColor = colors.neutral.darkMist,
  editable = true,
  isDisabled = false,
  testID,

  // Styling
  backgroundColor = colors.neutral.cotton,
  borderColor = colors.neutral.charcoal,
  borderWidth = 1,
  borderRadius = 8,
  height = 48,
  size = 'md',
  variant = 'outline',
  color = colors.neutral.charcoal,

  // Text styling
  fontSize,
  fontWeight = '100',
  fontFamily,

  // Layout
  margin,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  marginX,
  marginY,
  padding,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  paddingX,
  paddingY,

  // Focus/blur
  onFocus,
  onBlur,
  _focus,

  // Elements
  InputLeftElement,
  InputRightElement,

  // Web props
  _web,

  ...otherProps
}) => {
  const [isFocused, setIsFocused] = useState(false);

  // Size-based styling
  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return { height: 40, fontSize: 14, paddingHorizontal: 12 };
      case 'lg':
        return { height: 56, fontSize: 18, paddingHorizontal: 16 };
      default: // md
        return { height: 48, fontSize: 16, paddingHorizontal: 16 };
    }
  };

  const sizeStyles = getSizeStyles();

  // Focus state styling - prioritize _focus prop over internal state
  const getFocusStyles = () => {
    if (isFocused && _focus) {
      return {
        borderColor: _focus.borderColor || colors.secondary.orchid,
        backgroundColor: _focus.backgroundColor || backgroundColor,
      };
    }
    return {};
  };

  // Disabled state styling
  const getDisabledStyles = () => {
    if (isDisabled || !editable) {
      return {
        backgroundColor: colors.disabled.smoke || '#F5F5F5',
        opacity: 0.6,
      };
    }
    return {};
  };

  // Combine all style-related props
  const containerStyle: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: getDisabledStyles().backgroundColor || backgroundColor,
    borderColor: isFocused ? getFocusStyles().borderColor || colors.secondary.orchid : borderColor,
    borderWidth: typeof borderWidth === 'string' ? parseInt(borderWidth) : borderWidth,
    borderRadius: typeof borderRadius === 'string' ? parseInt(borderRadius) : borderRadius,
    height: typeof height === 'string' ? parseInt(height) : height,
    // Web-specific border fixes
    borderStyle: 'solid',
    boxSizing: 'border-box',
    ...(margin !== undefined && { margin }),
    ...(marginTop !== undefined && { marginTop }),
    ...(marginBottom !== undefined && { marginBottom }),
    ...(marginLeft !== undefined && { marginLeft }),
    ...(marginRight !== undefined && { marginRight }),
    ...(marginX !== undefined && { marginHorizontal: marginX }),
    ...(marginY !== undefined && { marginVertical: marginY }),
    ...getFocusStyles(),
    ...getDisabledStyles(),
  };

  const textInputStyle: TextStyle = {
    flex: 1,
    fontSize: fontSize || sizeStyles.fontSize,
    fontWeight: fontWeight,
    fontFamily: fontFamily,
    color: color,
    paddingHorizontal: paddingX !== undefined ? paddingX : sizeStyles.paddingHorizontal,
    paddingVertical: paddingY !== undefined ? paddingY : 12,
    // Web-specific TextInput fixes
    borderWidth: 0,
    outline: 'none',
    ...(padding !== undefined && { padding }),
    ...(paddingTop !== undefined && { paddingTop }),
    ...(paddingBottom !== undefined && { paddingBottom }),
    ...(paddingLeft !== undefined && { paddingLeft }),
    ...(paddingRight !== undefined && { paddingRight }),
  };

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  return (
    <View style={containerStyle} testID={testID}>
      {InputLeftElement && <View style={{ paddingLeft: 12 }}>{InputLeftElement}</View>}

      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={placeholderTextColor}
        editable={editable && !isDisabled}
        style={textInputStyle}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...otherProps}
      />

      {InputRightElement && <View style={{ paddingRight: 12 }}>{InputRightElement}</View>}
    </View>
  );
};

export default Input;
